import type { JSX } from 'react'
import type { ChannelProviderName } from './channels-types.js'

/**
 * Shared provider metadata (labels + icons) for the Channels admin UI.
 *
 * Previously duplicated between ChannelsPage.tsx (PROVIDER_ICONS/PROVIDER_LABELS,
 * small tab-sized icons) and ChannelProviderCard.tsx (PROVIDER_LABELS/PROVIDER_ICONS_LARGE,
 * large card-sized icons). Consolidated here so both import one source of truth.
 */

// Note: the two source files being deduplicated used slightly different
// casing for "WhatsApp"/"YouTube" (tab label vs. card heading). Preserved as
// two maps rather than one to avoid changing either component's visible text.

/** Tab-bar label casing (as previously defined in ChannelsPage.tsx). */
export const PROVIDER_LABELS: Record<ChannelProviderName, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  whatsapp: 'Whatsapp',
  youtube: 'Youtube',
  x: 'X',
}

/** Card-heading label casing (as previously defined in ChannelProviderCard.tsx). */
export const PROVIDER_CARD_LABELS: Record<ChannelProviderName, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  whatsapp: 'WhatsApp',
  youtube: 'YouTube',
  x: 'X',
}

/** Small icon variant — used in the provider tab bar. */
export const PROVIDER_ICONS: Record<ChannelProviderName, JSX.Element> = {
  facebook: <i className="pi pi-facebook text-[#1877F2] text-lg" />,
  instagram: <i className="pi pi-instagram text-[#E1306C] text-lg" />,
  whatsapp: <i className="pi pi-whatsapp text-[#25D366] text-lg" />,
  youtube: <i className="pi pi-youtube text-[#FF0000] text-lg" />,
  x: (
    <div className="bg-black text-white rounded-full w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold">
      X
    </div>
  ),
}

/** Large icon variant — used on the provider connect/status card header. */
export const PROVIDER_ICONS_LARGE: Record<ChannelProviderName, JSX.Element> = {
  facebook: (
    <div className="w-12 h-12 rounded-full bg-[#1877F2] text-white flex items-center justify-center text-3xl font-bold">
      f
    </div>
  ),
  instagram: (
    <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-[#FFDC80] via-[#F56040] to-[#C13584] text-white flex items-center justify-center text-2xl">
      <i className="pi pi-camera" />
    </div>
  ),
  whatsapp: (
    <div className="w-12 h-12 rounded-full bg-[#25D366] text-white flex items-center justify-center text-2xl">
      <i className="pi pi-whatsapp" />
    </div>
  ),
  youtube: (
    <div className="w-12 h-12 rounded-full bg-red-600 text-white flex items-center justify-center text-2xl">
      <i className="pi pi-youtube" />
    </div>
  ),
  x: (
    <div className="w-12 h-12 rounded-full bg-black text-white flex items-center justify-center text-2xl font-bold">
      X
    </div>
  ),
}
